import {ActionList, Heading, Tooltip, Spinner, Link, CounterLabel} from '@primer/react'
import styles from './GroupsList.module.css'
import {clsx} from 'clsx'
import {useRef, useState, useEffect} from 'react'
import {useFileTreeTooltip} from '@github-ui/use-file-tree-tooltip'
import {usePullRequestAnalytics} from '../../hooks/use-pull-request-analytics'
import {useChangeGroupsAnalytics} from '../../hooks/use-change-groups-analytics'
import type {Group} from '../../types/group-types'
import {GroupsFeedback, type FeedbackSubmitHandler} from '../groups-feedback/GroupsFeedback'
import {useChangeGroupsLiveUpdates} from '../../hooks/use-change-groups-live-updates'
import {
  useComparisonInfo,
  useChangeGroupGenerationChannel,
  useChangeGroupGenerationUnavailableReason,
  useChangeGroupGeneration,
  usePullRequestId,
} from '../../stores/selectors'
import {useChangeGroupsData} from '../../page-data/loaders/use-change-groups-data'
import {NegativeFeedbackForm} from '../groups-feedback/NegativeFeedbackForm'
import {useSubmitGroupsFeedbackMutation} from '../../mutations/use-submit-groups-feedback-mutation'
import {getChangeGroupGenerationUnavailableMessage} from '../../helpers/change-group-generation-availability'
import {ChevronDownIcon, ChevronUpIcon, XIcon} from '@primer/octicons-react'
import safeStorage from '@github-ui/safe-storage'
import {ssrSafeWindow} from '@github-ui/ssr-utils'

/**
 * Props for the GroupsList component.
 */
interface GroupsListProps {
  /** Array of groups to display in the list */
  groups: Group[]
  /** ID of the currently active/selected group, undefined if "All files" is selected */
  activeGroupId?: number
  /** Callback function called when a group is selected */
  onSelectGroup: (groupId: number) => void
  /** Callback function called when "All files" is selected to clear the active group */
  onClearActiveGroup: () => void
  /** Whether groups are loading */
  isLoadingState: boolean
}

/**
 * A list component that displays filterable groups for the file tree.
 * Renders an "All files" option followed by groups sorted by priority.
 * If no groups are provided, the component returns null.
 *
 * @returns The rendered groups list or null if no groups are provided
 */
export function GroupsList({
  groups,
  activeGroupId,
  onSelectGroup,
  onClearActiveGroup,
  isLoadingState,
}: GroupsListProps) {
  const {sendPullRequestAnalyticsEvent} = usePullRequestAnalytics()
  const {sendChangeGroupsPresentedEvent} = useChangeGroupsAnalytics()
  const {fullDiff} = useComparisonInfo()
  const changeGroupGenerationChannel = useChangeGroupGenerationChannel()
  const unavailableReason = useChangeGroupGenerationUnavailableReason()
  const pullRequestId = usePullRequestId()
  const groupsListToggleRef = useRef<HTMLButtonElement | null>(null)

  const {error} = useChangeGroupsData(fullDiff.headOid, fullDiff.baseOid)

  const showErrorState = !!error && !isLoadingState

  useChangeGroupsLiveUpdates(changeGroupGenerationChannel, fullDiff.baseOid, fullDiff.headOid)

  // Send analytics when groups are presented to the user
  useEffect(() => {
    if (!isLoadingState) {
      if (showErrorState || unavailableReason) {
        sendChangeGroupsPresentedEvent({
          isErrorState: true,
          unavailableReason: unavailableReason || undefined,
        })
      } else if (groups.length > 0) {
        sendChangeGroupsPresentedEvent({
          isErrorState: false,
          hasGroups: true,
        })
      } else {
        // Send analytics for empty state (no groups, no error, no unavailable reason)
        sendChangeGroupsPresentedEvent({
          isErrorState: false,
          hasGroups: false,
        })
      }
    }
  }, [groups, isLoadingState, showErrorState, unavailableReason, sendChangeGroupsPresentedEvent])

  const handleClearActiveGroup = () => {
    sendPullRequestAnalyticsEvent('groups_list.clear_group', 'GROUPS_LIST_ITEM')
    onClearActiveGroup()
  }

  const handleClearActiveGroupAndFocus = () => {
    handleClearActiveGroup()
    requestAnimationFrame(() => {
      groupsListToggleRef.current?.focus()
    })
  }

  const hasGroups = groups.length > 0

  const {isCollapsed, handleToggleCollapsed} = useGroupsListCollapseState(pullRequestId)

  // Collapse the groups and focus the toggle
  const toggleCollapsedAndFocus = () => {
    handleToggleCollapsed()
    requestAnimationFrame(() => {
      groupsListToggleRef.current?.focus()
    })
  }

  const heading = (
    <Heading as="h2" className="h6">
      Groups by Copilot
    </Heading>
  )

  return (
    <div className={clsx(styles.GroupsListContainer)}>
      {isLoadingState ? (
        <div className="d-flex flex-row flex-items-center fgColor-muted pt-2 px-2 pb-1">
          {heading}
          <span className="ml-auto d-flex flex-items-center">
            <Spinner size="small" aria-label="Loading change groups" />
          </span>
        </div>
      ) : (
        <button
          className={clsx('d-flex flex-row flex-items-center fgColor-muted', styles.GroupsListToggle)}
          onClick={handleToggleCollapsed}
          aria-expanded={!isCollapsed}
          ref={groupsListToggleRef}
        >
          {heading}
          {isCollapsed && hasGroups && (
            <CounterLabel scheme="secondary" className="mx-2">
              {groups.length}
            </CounterLabel>
          )}
          {isCollapsed ? <ChevronDownIcon className="ml-auto" /> : <ChevronUpIcon className="ml-auto" />}
        </button>
      )}
      {hasGroups && (
        <div className={clsx('mt-n2', styles.GroupsListItemsShiftLeft)}>
          {isCollapsed && !activeGroupId ? (
            // Maintain same spacing as error/loading states when collapsed
            <div className="pb-2" />
          ) : (
            <ActionList>
              {!isCollapsed && (
                <ActionList.Item active={activeGroupId === undefined} key="All files" onSelect={handleClearActiveGroup}>
                  All files
                </ActionList.Item>
              )}
              {groups
                .toSorted((a, b) => b.priority - a.priority)
                .map(group => (
                  <GroupItem
                    key={group.id}
                    group={group}
                    activeGroupId={activeGroupId}
                    onSelectGroup={onSelectGroup}
                    isListCollapsed={isCollapsed}
                    onClickGroupWhenCollapsed={handleClearActiveGroupAndFocus}
                  />
                ))
                .filter(Boolean)}
            </ActionList>
          )}
          {!isCollapsed && <GroupsFeedback className="ml-2" onNegativeFeedbackSubmit={toggleCollapsedAndFocus} />}
        </div>
      )}
      {!isCollapsed && !hasGroups && !isLoadingState && (
        <div className={'px-2 pb-2 pt-0 f6 fgColor-muted'}>
          {!unavailableReason ? (
            showErrorState && <p className="mb-0">No groups created</p>
          ) : (
            <>
              <p className="mb-0 d-inline">{getChangeGroupGenerationUnavailableMessage(unavailableReason)}</p>{' '}
              <FeedbackButton />
            </>
          )}
        </div>
      )}
    </div>
  )
}

function FeedbackButton() {
  const ref = useRef<HTMLButtonElement>(null)
  const {mutate: submitFeedback} = useSubmitGroupsFeedbackMutation()
  const [dialogOpen, setDialogOpen] = useState(false)

  const feedbackOptions = useChangeGroupGeneration().changeGroupFeedbackOptions

  const handleSubmitFeedback: FeedbackSubmitHandler = props => {
    submitFeedback(props)
    setDialogOpen(false)
  }

  return (
    <>
      <Link className="d-inline" as="button" ref={ref} inline muted onClick={() => setDialogOpen(true)}>
        Give feedback
      </Link>
      {dialogOpen && (
        <NegativeFeedbackForm
          feedbackOptions={feedbackOptions}
          onSubmit={handleSubmitFeedback}
          onClose={() => setDialogOpen(false)}
          returnFocusRef={ref}
          precheckOther
        />
      )}
    </>
  )
}

/**
 * Individual group item component that renders a single group in the list.
 * Includes tooltip functionality when the item is focused or hovered.
 */
function GroupItem({
  group,
  activeGroupId,
  isListCollapsed,
  onSelectGroup,
  onClickGroupWhenCollapsed,
}: {
  group: Group
  activeGroupId?: number
  isListCollapsed: boolean
  onSelectGroup: (groupId: number) => void
  onClickGroupWhenCollapsed: () => void
}) {
  const {sendPullRequestAnalyticsEvent} = usePullRequestAnalytics()
  const itemRef = useRef<HTMLLIElement>(null)
  const showTooltip = useFileTreeTooltip({focusRowRef: itemRef, mouseRowRef: itemRef})
  const handleSelect = () => {
    sendPullRequestAnalyticsEvent('groups_list.select_group', 'GROUPS_LIST_ITEM')
    onSelectGroup(group.id)
  }
  const isGroupActive = activeGroupId === group.id
  if (isListCollapsed && !isGroupActive) {
    return null
  }

  const listItem = (
    <ActionList.Item
      active={isGroupActive}
      key={group.name}
      onSelect={isListCollapsed ? onClickGroupWhenCollapsed : handleSelect}
      ref={itemRef}
    >
      <div className={clsx(styles.GroupItem, 'PRIVATE_TreeView-item-content-text')}>{group.name}</div>
      {isListCollapsed && (
        <ActionList.TrailingVisual>
          <XIcon />
        </ActionList.TrailingVisual>
      )}
    </ActionList.Item>
  )

  if (showTooltip) {
    return (
      <Tooltip text={group.name} direction="ne">
        {listItem}
      </Tooltip>
    )
  }

  return listItem
}

export function createGroupsListLocalStorageKey(pullRequestId: string) {
  return `GroupsListIsCollapsed_${pullRequestId}`
}

export function useGroupsListCollapseState(pullRequestId: string) {
  const safeLocalStorage = safeStorage('localStorage')
  const [isCollapsed, setIsCollapsed] = useState<boolean>(false)
  const {sendPullRequestAnalyticsEvent} = usePullRequestAnalytics()

  useEffect(() => {
    const storedState = safeLocalStorage.getItem(createGroupsListLocalStorageKey(pullRequestId))
    if (storedState !== null) {
      // We can't read from local storage during SSR, so we use useEffect to set the initial state on the client side

      setIsCollapsed(JSON.parse(storedState))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleToggleCollapsed = () => {
    const newIsCollapsed = !isCollapsed
    ssrSafeWindow?.requestAnimationFrame(() => {
      safeLocalStorage.setItem(createGroupsListLocalStorageKey(pullRequestId), JSON.stringify(newIsCollapsed))
    })
    setIsCollapsed(() => newIsCollapsed)
    sendPullRequestAnalyticsEvent('groups_list.toggle_collapse', 'GROUPS_LIST_TOGGLE', {is_collapsed: newIsCollapsed})
  }

  return {isCollapsed, handleToggleCollapsed}
}

try{ GroupsList.displayName ||= 'GroupsList' } catch {}
try{ FeedbackButton.displayName ||= 'FeedbackButton' } catch {}
try{ GroupItem.displayName ||= 'GroupItem' } catch {}